import React, { useState, useEffect } from 'react';
import { TextField, Button, CircularProgress } from '@mui/material';
import { message } from 'antd';
import { auth, db } from '../../firebase';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider, signInWithCredential } from 'firebase/auth';
import './ForgotPassword.css';
import { Link, useNavigate } from 'react-router-dom';
import Previous from '../../assets/previous-button.png';

function ForgotPassword() {
    const [username, setUsername] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loadingUsername, setLoadingUsername] = useState(false); // Loading for username verification
    const [loadingOtp, setLoadingOtp] = useState(false); // Loading for OTP sending
    const [loadingPassword, setLoadingPassword] = useState(false); // Loading for password reset
    const [otp, setOtp] = useState('');
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [verificationId, setVerificationId] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [showPasswordInput, setShowPasswordInput] = useState(false);
    const [recaptchaVerified, setRecaptchaVerified] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (phoneNumber && !recaptchaVerified) {
            setupRecaptcha();
        }
    }, [phoneNumber]);

    const handleUsernameSubmit = async () => {
        setLoadingUsername(true);
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('username', '==', username));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                message.error('Username not found');
                setLoadingUsername(false);
                return;
            }

            const userDoc = querySnapshot.docs[0];
            const userData = userDoc.data();
            const fullPhoneNumber = userData.phone;

            if (!fullPhoneNumber || !fullPhoneNumber.startsWith('+') || fullPhoneNumber.length < 10) {
                message.error('Invalid phone number in user data');
                setLoadingUsername(false);
                return;
            }

            setPhoneNumber(fullPhoneNumber);
            message.success('Phone number found, please verify to get OTP');
        } catch (error) {
            console.error('Error fetching user data:', error);
            message.error('Error fetching user data');
        } finally {
            setLoadingUsername(false);
        }
    };

    const setupRecaptcha = () => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                callback: (response) => {
                    setRecaptchaVerified(true);
                },
            });
            window.recaptchaVerifier.render();
        }
    };

    const sendOtp = async () => {
        setLoadingOtp(true);
        try {
            if (!phoneNumber) {
                throw new Error('Phone number is not available');
            }

            const appVerifier = window.recaptchaVerifier;
            const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
            setVerificationId(confirmationResult.verificationId);
            setShowOtpInput(true);
            message.success('OTP sent successfully');
        } catch (error) {
            console.error('Error sending OTP:', error);
            message.error('Error sending OTP: ' + error.message);
        } finally {
            setLoadingOtp(false);
        }
    };

    const verifyOtpAndResetPassword = async () => {
        setLoadingOtp(true); // Reusing OTP loading state
        try {
            const credential = PhoneAuthProvider.credential(verificationId, otp);
            await signInWithCredential(auth, credential);
            setShowPasswordInput(true);
        } catch (error) {
            console.error('Error verifying OTP:', error);
            message.error('Error verifying OTP: ' + error.message);
        } finally {
            setLoadingOtp(false);
        }
    };

    const resetPassword = async () => {
        setLoadingPassword(true);
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('username', '==', username));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                message.error('Username not found');
                setLoadingPassword(false);
                return;
            }

            const userDoc = querySnapshot.docs[0];
            const userDocRef = doc(db, 'users', userDoc.id);
            await updateDoc(userDocRef, { password: newPassword });
            navigate('/login');
            message.success('Password updated successfully');
        } catch (error) {
            console.error('Error updating password:', error);
            message.error('Error updating password: ' + error.message);
        } finally {
            setLoadingPassword(false);
        }
    };

    return (
        <div className="forgot-password">
            <div className='form-fp'>
                <div className='previous-icon login-link'>
                    <Link to={'/login'}>
                        <img style={{ width: '2rem' }} src={Previous} alt="Previous" />
                    </Link>
                </div>
                <h2>Forgot Password</h2>

                <TextField
                    label="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    fullWidth
                    margin="normal"
                    // inputProps={{
                    //     style: {
                    //         height: '7vh',
                    //     },
                    // }}
                />
                <Button onClick={handleUsernameSubmit} disabled={loadingUsername}>
                    {loadingUsername ? <CircularProgress size={24} /> : 'Verify Username'}
                </Button>

                {phoneNumber && (
                    <>
                        <p>Your phone number ends with **{phoneNumber.slice(-2)}</p>
                        <div id="recaptcha-container"></div>
                        <Button onClick={sendOtp} disabled={!recaptchaVerified || loadingOtp}>
                            {loadingOtp ? <CircularProgress size={24} /> : 'Get OTP'}
                        </Button>
                    </>
                )}

                {showOtpInput && (
                    <>
                        <TextField
                            label="OTP"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            fullWidth
                            margin="normal"
                            inputProps={{
                                style: {
                                    height: '7vh',
                                },
                            }}
                        />
                        <Button onClick={verifyOtpAndResetPassword} disabled={loadingOtp}>
                            {loadingOtp ? <CircularProgress size={24} /> : 'Verify OTP'}
                        </Button>
                    </>
                )}

                {showPasswordInput && (
                    <>
                        <TextField
                            label="New Password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            margin="normal"
                            inputProps={{
                                style: {
                                    height: '7vh',
                                },
                            }}
                        />
                        <Button onClick={resetPassword} disabled={loadingPassword}>
                            {loadingPassword ? <CircularProgress size={24} /> : 'Reset Password'}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

export default ForgotPassword;
